import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Check, X, Pencil } from "lucide-react";
import { 
  useQuizWithQuestions, 
  useUpdateQuiz, 
  useAddQuizQuestion, 
  useDeleteQuizQuestion,
  useUpdateQuizQuestion,
  useUpdateQuizOption,
  QuizQuestion 
} from "@/hooks/useQuizzes";

interface QuizBuilderProps {
  quizId: string;
  onBack: () => void;
}

export const QuizBuilder = ({ quizId, onBack }: QuizBuilderProps) => {
  const { data: quiz, isLoading } = useQuizWithQuestions(quizId);
  const updateQuiz = useUpdateQuiz();
  const addQuestion = useAddQuizQuestion();
  const deleteQuestion = useDeleteQuizQuestion();

  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingPassingScore, setIsEditingPassingScore] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPassingScore, setEditPassingScore] = useState(70);
  const [newQuestion, setNewQuestion] = useState({
    question_text: "",
    points: 1,
    options: [
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
    ],
  });

  if (isLoading) {
    return <div className="p-4 text-muted-foreground">Loading quiz...</div>;
  }

  if (!quiz) {
    return <div className="p-4 text-muted-foreground">Quiz not found</div>;
  }

  const handleStartEditTitle = () => {
    setEditTitle(quiz.title);
    setIsEditingTitle(true);
  };

  const handleSaveTitle = () => {
    if (editTitle.trim() && editTitle !== quiz.title) {
      updateQuiz.mutate({ id: quizId, title: editTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleStartEditDescription = () => {
    setEditDescription(quiz.description || "");
    setIsEditingDescription(true);
  };

  const handleSaveDescription = () => {
    if (editDescription !== (quiz.description || "")) {
      updateQuiz.mutate({ id: quizId, description: editDescription || null });
    }
    setIsEditingDescription(false);
  };

  const handleStartEditPassingScore = () => {
    setEditPassingScore(quiz.passing_score || 70);
    setIsEditingPassingScore(true);
  };

  const handleSavePassingScore = () => {
    if (editPassingScore !== quiz.passing_score) {
      updateQuiz.mutate({ id: quizId, passing_score: editPassingScore });
    }
    setIsEditingPassingScore(false);
  };

  const handleAddOption = () => {
    if (newQuestion.options.length < 6) {
      setNewQuestion({
        ...newQuestion,
        options: [...newQuestion.options, { option_text: "", is_correct: false }],
      });
    }
  };

  const handleRemoveOption = (index: number) => {
    if (newQuestion.options.length > 2) {
      const updatedOptions = newQuestion.options.filter((_, i) => i !== index);
      setNewQuestion({ ...newQuestion, options: updatedOptions });
    }
  };

  const handleOptionChange = (index: number, field: "option_text" | "is_correct", value: string | boolean) => {
    const updatedOptions = [...newQuestion.options];
    if (field === "is_correct") {
      // Toggle the correct status (allows multiple correct answers)
      updatedOptions[index] = { ...updatedOptions[index], is_correct: !updatedOptions[index].is_correct };
    } else if (field === "option_text") {
      updatedOptions[index] = { ...updatedOptions[index], option_text: value as string };
    }
    setNewQuestion({ ...newQuestion, options: updatedOptions });
  };

  const handleSaveQuestion = async () => {
    if (!newQuestion.question_text.trim()) return;
    if (!newQuestion.options.some(o => o.is_correct)) {
      return; // Must have at least one correct answer
    }
    if (newQuestion.options.some(o => !o.option_text.trim())) {
      return; // All options must have text
    }

    await addQuestion.mutateAsync({
      quiz_id: quizId,
      question_text: newQuestion.question_text,
      points: newQuestion.points,
      options: newQuestion.options,
    });

    setNewQuestion({
      question_text: "",
      points: 1,
      options: [
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
      ],
    });
    setShowAddQuestion(false);
  };

  const totalPoints = quiz.questions?.reduce((sum, q) => sum + q.points, 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Button variant="ghost" onClick={onBack} className="mb-2">
            ← Back to Quizzes
          </Button>
          
          {/* Editable Title */}
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="text-2xl font-bold h-auto py-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveTitle();
                  if (e.key === "Escape") setIsEditingTitle(false);
                }}
              />
              <Button size="sm" onClick={handleSaveTitle} disabled={updateQuiz.isPending}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditingTitle(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h2 className="text-2xl font-bold">{quiz.title}</h2>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleStartEditTitle}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* Editable Description */}
          {isEditingDescription ? (
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Add a description..."
                className="text-muted-foreground"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveDescription();
                  if (e.key === "Escape") setIsEditingDescription(false);
                }}
              />
              <Button size="sm" onClick={handleSaveDescription} disabled={updateQuiz.isPending}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditingDescription(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group mt-1">
              <p className="text-muted-foreground">
                {quiz.description || <span className="italic">No description</span>}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleStartEditDescription}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="published">Published</Label>
            <Switch
              id="published"
              checked={quiz.is_published}
              onCheckedChange={(checked) => updateQuiz.mutate({ id: quizId, is_published: checked })}
            />
          </div>
          <Badge variant="secondary">
            Total Points: {totalPoints}
          </Badge>
          
          {/* Editable Passing Score */}
          {isEditingPassingScore ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={0}
                max={100}
                value={editPassingScore}
                onChange={(e) => setEditPassingScore(parseInt(e.target.value) || 0)}
                className="w-20 h-8"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSavePassingScore();
                  if (e.key === "Escape") setIsEditingPassingScore(false);
                }}
              />
              <span className="text-sm">%</span>
              <Button size="sm" onClick={handleSavePassingScore} disabled={updateQuiz.isPending}>
                <Check className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditingPassingScore(false)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <Badge 
              variant="outline" 
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={handleStartEditPassingScore}
            >
              Passing: {quiz.passing_score}%
              <Pencil className="h-3 w-3 ml-1" />
            </Badge>
          )}
        </div>
      </div>

      <div className="grid gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Questions ({quiz.questions?.length || 0})</h3>
          <Dialog open={showAddQuestion} onOpenChange={setShowAddQuestion}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Question
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Question</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Question Text</Label>
                  <Textarea
                    value={newQuestion.question_text}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                    placeholder="Enter your question..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Points</Label>
                  <Input
                    type="number"
                    min={1}
                    value={newQuestion.points}
                    onChange={(e) => setNewQuestion({ ...newQuestion, points: parseInt(e.target.value) || 1 })}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Answer Options</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddOption}
                      disabled={newQuestion.options.length >= 6}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Option
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {newQuestion.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant={option.is_correct ? "default" : "outline"}
                          size="sm"
                          className="w-10 h-10 p-0"
                          onClick={() => handleOptionChange(index, "is_correct", true)}
                          title={option.is_correct ? "Correct answer" : "Mark as correct"}
                        >
                          {option.is_correct ? <Check className="h-4 w-4" /> : <span className="text-muted-foreground">{String.fromCharCode(65 + index)}</span>}
                        </Button>
                        <Input
                          value={option.option_text}
                          onChange={(e) => handleOptionChange(index, "option_text", e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          className="flex-1"
                        />
                        {newQuestion.options.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveOption(index)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Click the letter buttons to mark correct answers (multiple allowed)
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddQuestion(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveQuestion}
                    disabled={
                      !newQuestion.question_text.trim() ||
                      !newQuestion.options.some(o => o.is_correct) ||
                      newQuestion.options.some(o => !o.option_text.trim()) ||
                      addQuestion.isPending
                    }
                  >
                    Save Question
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {quiz.questions && quiz.questions.length > 0 ? (
          <div className="space-y-3">
            {quiz.questions.map((question, qIndex) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={qIndex}
                onDelete={() => deleteQuestion.mutate(question.id)}
              />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center text-muted-foreground">
              No questions yet. Add your first question to get started.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

interface QuestionCardProps {
  question: QuizQuestion;
  index: number;
  onDelete: () => void;
}

const QuestionCard = ({ question, index, onDelete }: QuestionCardProps) => {
  const updateQuestion = useUpdateQuizQuestion();
  const updateOption = useUpdateQuizOption();
  
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editPoints, setEditPoints] = useState(1);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [editOptionText, setEditOptionText] = useState("");

  const handleStartEditQuestion = () => {
    setEditQuestionText(question.question_text);
    setEditPoints(question.points);
    setIsEditingQuestion(true);
  };

  const handleSaveQuestion = () => {
    if (editQuestionText.trim()) {
      updateQuestion.mutate({
        id: question.id,
        question_text: editQuestionText.trim(),
        points: editPoints,
      });
    }
    setIsEditingQuestion(false);
  };

  const handleStartEditOption = (optionId: string, optionText: string) => {
    setEditingOptionId(optionId);
    setEditOptionText(optionText);
  };

  const handleSaveOption = () => {
    if (editingOptionId && editOptionText.trim()) {
      updateOption.mutate({
        id: editingOptionId,
        option_text: editOptionText.trim(),
      });
    }
    setEditingOptionId(null);
    setEditOptionText("");
  };

  const handleToggleCorrect = (optionId: string, currentIsCorrect: boolean) => {
    updateOption.mutate({
      id: optionId,
      is_correct: !currentIsCorrect,
    });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-medium shrink-0">
              {index + 1}
            </div>
            <div className="flex-1">
              {isEditingQuestion ? (
                <div className="space-y-2">
                  <Textarea
                    value={editQuestionText}
                    onChange={(e) => setEditQuestionText(e.target.value)}
                    className="min-h-[60px]"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setIsEditingQuestion(false);
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Points:</Label>
                    <Input
                      type="number"
                      min={1}
                      value={editPoints}
                      onChange={(e) => setEditPoints(parseInt(e.target.value) || 1)}
                      className="w-20 h-8"
                    />
                    <Button size="sm" onClick={handleSaveQuestion} disabled={updateQuestion.isPending}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setIsEditingQuestion(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="group">
                  <div className="flex items-start gap-2">
                    <CardTitle className="text-base font-medium flex-1">{question.question_text}</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={handleStartEditQuestion}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  <Badge variant="secondary" className="mt-1">
                    {question.points} point{question.points !== 1 ? "s" : ""}
                  </Badge>
                </div>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onDelete} className="shrink-0">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 ml-11">
          {question.options.map((option, oIndex) => (
            <div
              key={option.id}
              className={`flex items-center gap-2 p-2 rounded-md group ${
                option.is_correct 
                  ? "bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700" 
                  : "bg-muted/50"
              }`}
            >
              <button
                type="button"
                onClick={() => handleToggleCorrect(option.id, option.is_correct)}
                className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  option.is_correct 
                    ? "bg-green-600 text-white" 
                    : "bg-background hover:bg-primary hover:text-primary-foreground"
                }`}
                title={option.is_correct ? "Correct answer" : "Click to mark as correct"}
              >
                {option.is_correct ? <Check className="h-3 w-3" /> : String.fromCharCode(65 + oIndex)}
              </button>
              
              {editingOptionId === option.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input
                    value={editOptionText}
                    onChange={(e) => setEditOptionText(e.target.value)}
                    className="h-8"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveOption();
                      if (e.key === "Escape") {
                        setEditingOptionId(null);
                        setEditOptionText("");
                      }
                    }}
                  />
                  <Button size="sm" onClick={handleSaveOption} disabled={updateOption.isPending}>
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => {
                    setEditingOptionId(null);
                    setEditOptionText("");
                  }}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="flex-1">{option.option_text}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                    onClick={() => handleStartEditOption(option.id, option.option_text)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
